// Stock movement → ledger postings.
// Each movement on inventory_items also writes journal entries so the books
// stay consistent. Sales hit cash + revenue (and optionally COGS + inventory
// if cost_price is set). Receipts hit inventory + cash (or trade creditors).

import type { Account } from '@nako/types';
import { supabase } from './supabase';
import { localYmd } from './journal-postings';

type AccountType = 'asset' | 'liability' | 'equity' | 'income' | 'expense';

export interface PostingResult {
  ok: boolean;
  error?: string;
  journal_entry_id?: string;
}

interface Item {
  id: string;
  business_id: string;
  name: string;
  qty_on_hand: number;
  cost_price: number | null;
  sale_price: number | null;
  /** SKU of a consumable that's drawn down with each unit sold (e.g. clays
   *  bundled with ammo, fries with a burger combo). */
  bundled_sku?: string | null;
  /** Quantity of bundled consumable per unit sold. */
  bundled_qty?: number | null;
}

async function fetchAccounts(business_id: string): Promise<Account[]> {
  const { data } = await supabase.from('accounts').select('*').eq('business_id', business_id);
  return (data ?? []) as Account[];
}

function findByGl(accounts: Account[], code: string): Account | undefined {
  return accounts.find((a) => a.gl_code === code);
}

function firstOfType(accounts: Account[], type: AccountType): Account | undefined {
  return accounts.find((a) => a.type === type);
}

async function insertEntryWithLines(args: {
  business_id: string;
  user_id: string | null;
  date: string;
  description: string;
  source: 'sale' | 'purchase' | 'manual' | 'adjustment';
  source_id: string;
  lines: Array<{ account_id: string; debit: number; credit: number; description: string }>;
}): Promise<string | null> {
  // Assert the entry balances before writing the header so we never commit an
  // orphaned, unbalanced entry (Round-2 finding #1).
  const totDebit = round2(args.lines.reduce((s, l) => s + l.debit, 0));
  const totCredit = round2(args.lines.reduce((s, l) => s + l.credit, 0));
  if (args.lines.length < 2 || totDebit !== totCredit || totDebit <= 0) {
    console.error('refusing to post unbalanced entry', args.description, totDebit, totCredit);
    return null;
  }

  const { data: entry, error } = await supabase
    .from('journal_entries')
    .insert({
      business_id: args.business_id,
      entry_date: args.date,
      reference: args.description,
      description: args.description,
      source: args.source,
      source_id: args.source_id,
      is_posted: true,
      posted_at: new Date().toISOString(),
      created_by: args.user_id,
    })
    .select()
    .single();

  if (error || !entry) {
    console.error('journal_entries insert failed', error);
    return null;
  }

  const eid = (entry as { id: string }).id;
  const lineRows = args.lines.map((l) => ({
    entry_id: eid,
    account_id: l.account_id,
    debit: l.debit,
    credit: l.credit,
    vat_amount: 0,
    vat_rate: 0,
    description: l.description,
  }));
  const { error: linesErr } = await supabase.from('journal_lines').insert(lineRows);
  // Lines failed but the header committed (separate request) — delete the
  // orphan so it can't corrupt the trial balance.
  if (linesErr) {
    console.error('journal_lines insert failed', linesErr);
    await supabase.from('journal_entries').delete().eq('id', eid);
    return null;
  }

  return eid;
}

/** Side-effect: draw down a bundled consumable SKU on every unit sold. */
async function decrementBundled(args: {
  business_id: string;
  bundled_sku: string;
  qty: number;
}): Promise<{ name: string; deducted: number; remaining: number } | null> {
  const { data: bundled } = await supabase
    .from('inventory_items')
    .select('*')
    .eq('business_id', args.business_id)
    .eq('sku', args.bundled_sku)
    .maybeSingle();
  if (!bundled) return null;

  const b = bundled as { id: string; name: string; qty_on_hand: number };
  const newQty = Math.max(0, b.qty_on_hand - args.qty);
  await supabase.from('inventory_items').update({ qty_on_hand: newQty }).eq('id', b.id);
  return { name: b.name, deducted: args.qty, remaining: newQty };
}

/**
 * Sell stock: decrement qty, post Cash <- Sales (and COGS <- Inventory if costed).
 * If the item has a bundled consumable (e.g. ammo bundles 25 clays per case),
 * also draw down that companion SKU's stock.
 */
export async function postSale(args: {
  item: Item;
  qty: number;
  totalPrice: number;
  user_id: string | null;
  date?: string;
  income_gl_code?: string; // e.g. '4000' for Ammo Sales
  vat_registered?: boolean; // when true, totalPrice is treated as VAT-inclusive (14%)
}): Promise<PostingResult & { bundled?: { name: string; deducted: number; remaining: number } }> {
  const date = args.date ?? localYmd();
  if (args.qty <= 0) return { ok: false, error: 'Quantity must be positive' };
  if (args.qty > args.item.qty_on_hand) {
    return {
      ok: false,
      error: `Only ${args.item.qty_on_hand} on hand — cannot sell ${args.qty}.`,
    };
  }

  const accounts = await fetchAccounts(args.item.business_id);
  const cash = findByGl(accounts, '1000');
  const inventoryAcc = findByGl(accounts, '1200');
  const vatOut = findByGl(accounts, '2100');

  // Income: Sales Revenue (4000) explicitly; fall back to a passed GL, then type.
  const income =
    findByGl(accounts, '4000') ??
    (args.income_gl_code ? findByGl(accounts, args.income_gl_code) : undefined) ??
    firstOfType(accounts, 'income');

  // COGS: Cost of Sales (5000) explicitly — never firstOfType('expense'),
  // which can grab Salaries (5100) and corrupt the P&L.
  const cogs = findByGl(accounts, '5000') ?? firstOfType(accounts, 'expense');

  if (!cash || !income) {
    return { ok: false, error: 'Cash or Income account missing — seed COA first.' };
  }

  // Decrement stock
  const newQty = args.item.qty_on_hand - args.qty;
  await supabase.from('inventory_items').update({ qty_on_hand: newQty }).eq('id', args.item.id);

  // Post Dr Cash / Cr Sales (+ Cr VAT Output when VAT-registered; totalPrice is
  // treated as VAT-inclusive at 14%).
  const net = args.vat_registered ? round2(args.totalPrice / 1.14) : args.totalPrice;
  const vat = args.vat_registered ? round2(args.totalPrice - net) : 0;
  const lines: Array<{ account_id: string; debit: number; credit: number; description: string }> = [
    {
      account_id: cash.id,
      debit: args.totalPrice,
      credit: 0,
      description: `Sale: ${args.qty} × ${args.item.name}`,
    },
    {
      account_id: income.id,
      debit: 0,
      credit: net,
      description: `Sale: ${args.qty} × ${args.item.name}`,
    },
  ];
  if (vat > 0 && vatOut) {
    lines.push({
      account_id: vatOut.id,
      debit: 0,
      credit: vat,
      description: `VAT output: ${args.item.name}`,
    });
  }

  // If costed, also Dr COGS / Cr Inventory at cost
  if (args.item.cost_price && args.item.cost_price > 0 && cogs && inventoryAcc) {
    const costAmount = round2(args.item.cost_price * args.qty);
    lines.push(
      {
        account_id: cogs.id,
        debit: costAmount,
        credit: 0,
        description: `COGS: ${args.qty} × ${args.item.name}`,
      },
      {
        account_id: inventoryAcc.id,
        debit: 0,
        credit: costAmount,
        description: `Inventory release: ${args.qty} × ${args.item.name}`,
      },
    );
  }

  const entryId = await insertEntryWithLines({
    business_id: args.item.business_id,
    user_id: args.user_id,
    date,
    description: `Sale of ${args.item.name}`,
    source: 'sale',
    source_id: args.item.id,
    lines,
  });

  // Bundled consumable side-effect (no GL impact — already expensed)
  let bundledResult: { name: string; deducted: number; remaining: number } | null = null;
  if (args.item.bundled_sku && args.item.bundled_qty && args.item.bundled_qty > 0) {
    bundledResult = await decrementBundled({
      business_id: args.item.business_id,
      bundled_sku: args.item.bundled_sku,
      qty: args.qty * args.item.bundled_qty,
    });
  }

  return {
    ok: true,
    journal_entry_id: entryId ?? undefined,
    bundled: bundledResult ?? undefined,
  };
}

/**
 * Receive stock: increment qty, post Inventory <- Cash. Updates cost_price
 * to a weighted average if cost_price already set.
 */
export async function postReceive(args: {
  item: Item;
  qty: number;
  unitCost: number;
  user_id: string | null;
  date?: string;
}): Promise<PostingResult> {
  const date = args.date ?? localYmd();
  if (args.qty <= 0) return { ok: false, error: 'Quantity must be positive' };
  if (args.unitCost < 0) return { ok: false, error: 'Unit cost cannot be negative' };

  const accounts = await fetchAccounts(args.item.business_id);
  const cash = findByGl(accounts, '1000');
  const inventoryAcc = findByGl(accounts, '1200');

  if (!cash || !inventoryAcc) {
    return { ok: false, error: 'Cash or Inventory account missing.' };
  }

  // Weighted-average cost
  const oldQty = args.item.qty_on_hand;
  const oldCost = args.item.cost_price ?? 0;
  const newQty = oldQty + args.qty;
  const newCost =
    newQty === 0 ? oldCost : round2((oldQty * oldCost + args.qty * args.unitCost) / newQty);

  await supabase
    .from('inventory_items')
    .update({
      qty_on_hand: newQty,
      cost_price: newCost,
    })
    .eq('id', args.item.id);

  const total = round2(args.qty * args.unitCost);
  const entryId = await insertEntryWithLines({
    business_id: args.item.business_id,
    user_id: args.user_id,
    date,
    description: `Stock receipt: ${args.item.name}`,
    source: 'purchase',
    source_id: args.item.id,
    lines: [
      {
        account_id: inventoryAcc.id,
        debit: total,
        credit: 0,
        description: `Receive ${args.qty} × ${args.item.name} @ ${args.unitCost.toFixed(2)}`,
      },
      {
        account_id: cash.id,
        debit: 0,
        credit: total,
        description: `Stock receipt payment`,
      },
    ],
  });

  return { ok: true, journal_entry_id: entryId ?? undefined };
}

/**
 * Opening stock entered when an item is first created (qty + cost on the
 * Add-item form). Posts Dr Inventory (1200) / Cr Owner's Capital (3000) so the
 * Inventory account actually carries the value — without this, the first sale's
 * COGS release (Cr 1200) would drive Inventory negative because the opening
 * balance was never debited. Stock brought in this way is treated as a capital
 * contribution in kind by the owner.
 */
export async function postOpeningStock(args: {
  item: Item;
  qty: number;
  unitCost: number;
  user_id: string | null;
  date?: string;
}): Promise<PostingResult> {
  const date = args.date ?? localYmd();
  if (args.qty <= 0 || args.unitCost <= 0) {
    // Nothing to value — no posting (consistent: an uncosted item also posts no
    // COGS on sale, so the ledger stays balanced).
    return { ok: true };
  }

  const accounts = await fetchAccounts(args.item.business_id);
  const inventoryAcc = findByGl(accounts, '1200');
  const capital = findByGl(accounts, '3000') ?? firstOfType(accounts, 'equity');

  if (!inventoryAcc || !capital) {
    return { ok: false, error: 'Inventory or Owner’s Capital account missing.' };
  }

  const total = round2(args.qty * args.unitCost);
  const entryId = await insertEntryWithLines({
    business_id: args.item.business_id,
    user_id: args.user_id,
    date,
    description: `Opening stock: ${args.item.name}`,
    source: 'adjustment',
    source_id: args.item.id,
    lines: [
      {
        account_id: inventoryAcc.id,
        debit: total,
        credit: 0,
        description: `Opening stock ${args.qty} × ${args.item.name} @ ${args.unitCost.toFixed(2)}`,
      },
      {
        account_id: capital.id,
        debit: 0,
        credit: total,
        description: `Opening stock brought in by owner: ${args.item.name}`,
      },
    ],
  });

  return { ok: true, journal_entry_id: entryId ?? undefined };
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
